import type { AdminViewDefinition } from "../../components/admin-components.js";

export const operationAdminViews: AdminViewDefinition[] = [
  installationDashboardView(),
  dataBrowserView(),
  bedrockPromptDiagnosticsView(),
  extractionDiagnosticsView(),
  incidentControlsView(),
  releaseComplianceView(),
  adminSettingsView(),
  partialServiceDegradationView(),
  protectedDataGateBlockedView(),
];

export function installationDashboardView(): AdminViewDefinition {
  return {
    id: "installation-dashboard",
    navId: "operations",
    title: "Installation & Operations Overview",
    description:
      "Monitor installed stores, integration health, job failures, usage, and production safety.",
    actions: [
      { label: "Refresh dashboard" },
      { label: "Open data browser", variant: "soft", routeId: "data-browser" },
      { label: "Run Bedrock prompt", variant: "soft", routeId: "bedrock-prompt-diagnostics" },
      { label: "Run product-info diagnostic", variant: "soft", routeId: "extraction-diagnostics" },
      { label: "Open incident controls", variant: "soft", routeId: "incident-feature-controls" },
    ],
    stats: operationsStats(),
  };
}

function dataBrowserView(): AdminViewDefinition {
  return {
    id: "data-browser",
    navId: "data-browser",
    title: "DynamoDB Data Browser",
    description:
      "Query sanitized Price Rescue records by model, store, date, and safe identifiers without using DynamoDB Scan.",
    actions: [{ label: "Back to overview", variant: "soft", routeId: "installation-dashboard" }],
  };
}

function bedrockPromptDiagnosticsView(): AdminViewDefinition {
  return {
    id: "bedrock-prompt-diagnostics",
    navId: "bedrock-prompt-diagnostics",
    title: "Bedrock Prompt Diagnostics",
    description:
      "Send a one-off prompt through the worker Bedrock client to verify model communication without creating application records.",
    actions: [{ label: "Back to overview", variant: "soft", routeId: "installation-dashboard" }],
  };
}

function extractionDiagnosticsView(): AdminViewDefinition {
  return {
    id: "extraction-diagnostics",
    navId: "extraction-diagnostics",
    title: "Product Info Diagnostics",
    description:
      "Run a safe one-off external product-info extraction inside the worker runtime without creating requests, jobs, or merchant evidence.",
    actions: [{ label: "Back to overview", variant: "soft", routeId: "installation-dashboard" }],
  };
}

function incidentControlsView(): AdminViewDefinition {
  return {
    id: "incident-feature-controls",
    navId: "incidents",
    title: "Incident & Feature Controls",
    description:
      "Manage audited environment, store, domain, and module safety controls with explicit reasons and expiration.",
    actions: [{ label: "Enable kill switch", variant: "danger" }, { label: "View runbooks" }],
    stats: [
      { label: "Active controls", value: "2", detail: "1 store · 1 domain", tone: "red" },
      { label: "Open incidents", value: "2", detail: "Both acknowledged", tone: "orange" },
      { label: "Controls expiring", value: "1", detail: "In 4h 22m", tone: "blue" },
      {
        label: "Last safety review",
        value: "18m ago",
        detail: "All service guards active",
        tone: "green",
      },
    ],
  };
}

function releaseComplianceView(): AdminViewDefinition {
  return {
    id: "release-compliance",
    navId: "release",
    title: "Release & Compliance Readiness",
    description:
      "Verify platform protected-data approval, privacy callbacks, minimized contracts, and production release gates.",
    actions: [
      {
        label: "Run compliance verification",
        variant: "primary",
        disabled: true,
        disabledReason: "Release readiness does not have a live Admin API yet.",
      },
      {
        label: "View latest test report",
        disabled: true,
        disabledReason: "Release test report retrieval is not wired yet.",
      },
    ],
    stats: [
      {
        label: "Release gate",
        value: "Manual",
        detail: "Use release checklist",
        tone: "orange",
      },
      {
        label: "Privacy callbacks",
        value: "Manual",
        detail: "Not loaded from API",
        tone: "orange",
      },
      {
        label: "Field approval",
        value: "Manual",
        detail: "Confirm platform approval",
        tone: "orange",
      },
      {
        label: "Redaction evidence",
        value: "Manual",
        detail: "Use redaction reports",
        tone: "orange",
      },
    ],
  };
}

function adminSettingsView(): AdminViewDefinition {
  return {
    id: "admin-settings",
    navId: "settings",
    title: "Admin Settings",
    description:
      "Review your identity, session security, environment access, and internal permissions.",
    actions: [{ label: "Log out", variant: "danger" }],
  };
}

function partialServiceDegradationView(): AdminViewDefinition {
  return {
    ...installationDashboardView(),
    id: "partial-service-degradation",
    callouts: [
      {
        title: "Partial service degradation",
        body: "Bedrock normalization is degraded. Automatic offers continue only when deterministic verification is complete.",
        tone: "orange",
      },
    ],
  };
}

function protectedDataGateBlockedView(): AdminViewDefinition {
  return {
    ...releaseComplianceView(),
    id: "protected-data-gate-blocked",
    stats: [
      { label: "Release gate", value: "Blocked", detail: "Manual approval required", tone: "red" },
      {
        label: "Privacy callbacks",
        value: "Manual",
        detail: "Not loaded from API",
        tone: "orange",
      },
      {
        label: "Field approval",
        value: "Manual",
        detail: "Confirm platform approval",
        tone: "orange",
      },
      {
        label: "Redaction evidence",
        value: "Manual",
        detail: "Use redaction reports",
        tone: "orange",
      },
    ],
    callouts: [
      {
        title: "Protected data gate blocked",
        body: "Release cannot proceed until protected customer data checks are approved.",
        tone: "red",
      },
    ],
  };
}

function operationsStats() {
  return [
    { label: "Active stores", value: "148", detail: "+6 this month", tone: "green" as const },
    {
      label: "Installing / syncing",
      value: "4",
      detail: "2 need attention",
      tone: "blue" as const,
    },
    { label: "Suspended", value: "2", detail: "Both incident-related", tone: "orange" as const },
    { label: "Failed jobs", value: "7", detail: "3 retryable now", tone: "red" as const },
    {
      label: "Unhealthy domains",
      value: "3",
      detail: "Automatic offers blocked",
      tone: "purple" as const,
    },
  ];
}
